import { useState, useEffect, useCallback, Fragment } from 'react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Storefront, Copy, Pencil, Trash, MagnifyingGlass, ShieldStar, Warning, Plus, Prohibit, UploadSimple, ChartBar, ClockCounterClockwise, ArrowCounterClockwise, FileXls } from '@phosphor-icons/react';
import { getConsolidatedAccounts, updatePlatformAccount, deletePlatformAccount, createPlatformAccount, getAmazonAutoLockSetting, toggleAmazonAutoLock } from '../lib/api';
import api from '../lib/api';
import LoginPasswordWidget from '../components/LoginPasswordWidget';
import { formatDateTime } from '../lib/utils';

/**
 * Consolidated Amazon / TataNeu account stock — admin only.
 * - 2 tabs (Amazon | TataNeu)
 * - Email-based auto-dedup (case-insensitive, trim spaces)
 * - Columns: Email | Password | TOTP Secret | Login (pwd+OTP merged) | Extras | Usage
 * - Edit dialog (admin can change password/TOTP)
 * - Delete (soft delete, archived)
 */
export default function AmazonAccountStockPage() {
  const [tab, setTab] = useState('amazon');
  const [data, setData] = useState({ items: [], total_unique: 0, duplicates_skipped: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // {id, raw_data}
  const [editFields, setEditFields] = useState({});
  const [deleting, setDeleting] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ Acount: '', Pasword: '', 'OTP Cod': '', Mobile: '' });
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [assignHistory, setAssignHistory] = useState([]);
  const [showAssignHistory, setShowAssignHistory] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [amazonLockEnabled, setAmazonLockEnabled] = useState(true);
  const [amazonLockLoading, setAmazonLockLoading] = useState(false);

  const loadAmazonLock = useCallback(async () => {
    try {
      const { data } = await getAmazonAutoLockSetting();
      setAmazonLockEnabled(!!data.enabled);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadAmazonLock(); }, [loadAmazonLock]);

  const handleAmazonLockToggle = async () => {
    const next = !amazonLockEnabled;
    setAmazonLockLoading(true);
    try {
      await toggleAmazonAutoLock(next);
      setAmazonLockEnabled(next);
      toast.success(next
        ? '✅ Auto-Lock ON — naye file assignments par accounts lock honge'
        : '⏸️ Auto-Lock OFF — koi Amazon account assign nahi hoga');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toggle failed');
    } finally { setAmazonLockLoading(false); }
  };

  const loadAssignHistory = useCallback(async () => {
    try {
      const { data } = await api.get('/platform-accounts/assignment-history', {
        params: { limit: 100 },
      });
      setAssignHistory(data?.items || []);
    } catch {
      // soft-fail
    }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        api.get('/platform-accounts/stock-summary', { params: { platform: tab } }),
        api.get('/platform-accounts/usage-history', { params: { platform: tab, limit: 200 } }),
      ]);
      setSummary(s.data);
      setHistory(h.data?.items || []);
    } catch {
      // soft-fail
    }
  }, [tab]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/platform-accounts/upload?platform=${tab}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const inserted = data?.inserted ?? data?.count ?? 'Some';
      toast.success(`✅ ${inserted} accounts uploaded — duplicates auto-skipped on consolidated view`);
      load();
      loadSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fail');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Specialized "Amazon Account with 2FA" multi-sheet upload
  const handleUpload2FA = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Step 1: dry-run preview
      const { data: dry } = await api.post('/platform-accounts/upload-amazon-2fa?dry_run=true&sample_size=2', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const summary = `📊 Preview:\n• Amazon parsed: ${dry.amazon.raw_rows.toLocaleString()}\n• Amazon fresh: ${dry.amazon.fresh_to_insert.toLocaleString()}\n• Gmail parsed: ${dry.gmail.raw_rows}\n• Gmail fresh: ${dry.gmail.fresh_to_insert}\n• Skipped sheets: ${dry.sheets_skipped.join(', ') || 'none'}\n\n⚠️ ${dry.amazon.fresh_to_insert + dry.gmail.fresh_to_insert} entries DB me daalega. Confirm karna hai?`;
      if (!window.confirm(summary)) {
        toast.info('Cancelled');
        return;
      }
      // Step 2: actual commit
      const fd2 = new FormData();
      fd2.append('file', file);
      const { data: out } = await api.post('/platform-accounts/upload-amazon-2fa?dry_run=false', fd2, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`✅ ${out.inserted_amazon} Amazon + ${out.inserted_gmail} Gmail accounts inserted`);
      load();
      loadSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fail');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRollback = async () => {
    const platform = window.prompt(
      'Bulk-uploaded accounts ko rollback karna hai? Type:\n• "amazon" — only Amazon\n• "gmail" — only Gmail\n• "both" — dono\n• Cancel for no action',
      'both',
    );
    if (!platform || !['amazon', 'gmail', 'both'].includes(platform.toLowerCase())) {
      return;
    }
    if (!window.confirm(`⚠️ Sure? Soft-delete ALL bulk-uploaded ${platform} accounts. Reverse karna mushkil hoga.`)) return;
    try {
      const { data } = await api.post(`/platform-accounts/upload-amazon-2fa/rollback?platform=${platform.toLowerCase()}&confirm=YES_DELETE_ALL`);
      toast.success(`🗑️ ${data.deleted} accounts soft-deleted`);
      load();
      loadSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rollback fail');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await getConsolidatedAccounts(tab);
      setData(resp);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load fail');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
    loadSummary();
  }, [load, loadSummary]);

  const items = data.items || [];
  const filtered = search
    ? items.filter(i =>
        (i.email || '').toLowerCase().includes(search.toLowerCase()) ||
        (i.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (i.mobile || '').includes(search)
      )
    : items;
  // Render cap (perf safety for 22k+ rows). Search results bypass cap if smaller.
  const RENDER_CAP = 200;
  const displayed = showAll ? filtered : filtered.slice(0, RENDER_CAP);

  const copy = (val, lbl) => {
    if (!val) return toast.error(`${lbl} blank hai`);
    navigator.clipboard.writeText(val);
    toast.success(`${lbl} copied`);
  };

  const openEdit = (item) => {
    setEditing(item);
    setEditFields({ ...item.raw_data });
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await updatePlatformAccount(editing.id, editFields);
      toast.success('Account updated');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update fail');
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deletePlatformAccount(deleting.id);
      toast.success('Account deleted (archived)');
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete fail');
    }
  };

  // Live duplicate check while typing email in Add form
  const addEmailLower = (addForm.Acount || '').trim().toLowerCase();
  const addEmailDuplicate = !!addEmailLower && items.some(i => (i.email || '').toLowerCase() === addEmailLower);

  const submitAdd = async () => {
    const email = (addForm.Acount || '').trim();
    if (!email) return toast.error('Email required');
    if (addEmailDuplicate) return toast.error('Yeh email already stock me hai');
    try {
      // Strip empty fields
      const data = Object.fromEntries(Object.entries(addForm).filter(([, v]) => (v || '').toString().trim()));
      await createPlatformAccount({ platform: tab, data });
      toast.success(`✅ ${tab === 'amazon' ? 'Amazon' : 'TataNeu'} account added to stock`);
      setAdding(false);
      setAddForm({ Acount: '', Pasword: '', 'OTP Cod': '', Mobile: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Add fail');
    }
  };

  const flagPwdInvalid = async (item) => {
    if (!window.confirm(`Sure? Account "${item.email}" ka password invalid mark hoga aur 30-day cooldown lag jayega.`)) return;
    try {
      await api.post(`/platform-accounts/${item.id}/flag-password-invalid`, { reason: 'Admin marked from stock page' });
      toast.success('Flagged + replacement request created');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Flag fail');
    }
  };

  const tabPalette = tab === 'amazon'
    ? { tag: 'bg-orange-600', tagSoft: 'bg-orange-50 text-orange-700 border-orange-200', label: '🟠 AMAZON' }
    : { tag: 'bg-pink-600', tagSoft: 'bg-pink-50 text-pink-700 border-pink-200', label: '🔴 TATANEU' };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Storefront size={24} className="text-violet-600" weight="bold" />
            Account Stock — Consolidated View
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Sara Amazon &amp; TataNeu accounts ek jagah · email-based auto-dedup · admin-only access
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input id="aas-upload-input" type="file" accept=".xlsx,.xls,.csv,.txt" onChange={handleUpload} className="hidden" data-testid="aas-upload-input" />
          <input id="aas-upload-2fa-input" type="file" accept=".xlsx,.xls" onChange={handleUpload2FA} className="hidden" data-testid="aas-upload-2fa-input" />
          <Button
            variant="outline"
            onClick={() => document.getElementById('aas-upload-input').click()}
            disabled={uploading}
            className="border-violet-300 text-violet-700 hover:bg-violet-50"
            data-testid="aas-upload-btn"
          >
            <UploadSimple size={16} className="mr-1" /> {uploading ? 'Uploading…' : 'Upload File'}
          </Button>
          {tab === 'amazon' && (
            <>
              <Button
                variant="outline"
                onClick={() => document.getElementById('aas-upload-2fa-input').click()}
                disabled={uploading}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="aas-upload-2fa-btn"
                title="Specialized parser for the 'Amazon Account with 2FA' multi-sheet Excel"
              >
                <FileXls size={16} className="mr-1" /> {uploading ? '…' : 'Upload 2FA File'}
              </Button>
              <Button
                variant="outline"
                onClick={handleRollback}
                disabled={uploading}
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="aas-rollback-btn"
                title="Soft-delete all bulk-uploaded accounts (preview cleanup)"
              >
                <ArrowCounterClockwise size={16} className="mr-1" /> Rollback Bulk
              </Button>
            </>
          )}
          <Button onClick={() => setAdding(true)} className="bg-violet-600 hover:bg-violet-700" data-testid="aas-add-btn">
            <Plus size={16} className="mr-1" /> Add {tab === 'amazon' ? 'Amazon' : 'TataNeu'}
          </Button>
        </div>
      </div>

      {/* Amazon Auto-Lock master toggle — admin can pause/resume account assignment from this page */}
      {tab === 'amazon' && (
        <div className={`rounded-lg border p-3 flex items-center justify-between gap-3 flex-wrap ${amazonLockEnabled ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-400'}`} data-testid="aas-amazon-lock-banner">
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${amazonLockEnabled ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'}`}>
              {amazonLockEnabled ? '🔒' : '⏸️'}
            </span>
            <div>
              <div className="text-sm font-bold text-gray-900">
                Amazon Account Auto-Assignment: {amazonLockEnabled ? <span className="text-emerald-700">ENABLED</span> : <span className="text-amber-700">PAUSED</span>}
              </div>
              <div className="text-xs text-gray-700 mt-0.5 max-w-2xl">
                {amazonLockEnabled
                  ? 'File assign hone par staff ko Amazon accounts auto-lock ho rahe hain. Agar Amazon kaam band karna hai, pause kar do.'
                  : 'Abhi koi naya assignment Amazon account nahi locks karega. Jab Amazon kaam start ho, "Resume" karke ON kar do — naye files se direct lock hone lagenge.'}
              </div>
            </div>
          </div>
          <Button
            onClick={handleAmazonLockToggle}
            disabled={amazonLockLoading}
            className={amazonLockEnabled ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}
            data-testid="aas-amazon-lock-toggle"
          >
            {amazonLockLoading ? '…' : (amazonLockEnabled ? '⏸️ Pause Auto-Lock' : '▶️ Resume Auto-Lock')}
          </Button>
        </div>
      )}

      {/* Pool Health Pill — always-visible at-a-glance health indicator */}
      {summary && (
        <div className="sticky top-0 z-20 -mx-2 px-3 py-1.5 mb-1 backdrop-blur-md bg-white/85 border border-gray-200 rounded-lg shadow-sm flex items-center gap-2 text-[12px] flex-wrap" data-testid="aas-pool-health">
          <span className="font-bold text-gray-700 mr-1">Pool Health:</span>
          <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2.5 py-0.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            <b className="font-bold">{summary.available_now?.toLocaleString('en-IN') || 0}</b> free
          </span>
          <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 border border-blue-200 rounded-full px-2.5 py-0.5" title={(summary.top_users || []).map(u => `${u.name}: ${u.active_count}`).join('\n')}>
            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
            <b className="font-bold">{summary.held_active || 0}</b> held by <b>{(summary.top_users || []).length}</b> {(summary.top_users || []).length === 1 ? 'user' : 'users'}
          </span>
          {summary.in_cooldown > 0 && (
            <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2.5 py-0.5">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              <b className="font-bold">{summary.in_cooldown}</b> cooldown
            </span>
          )}
          {summary.password_invalid > 0 && (
            <span className="inline-flex items-center gap-1 bg-red-50 text-red-800 border border-red-200 rounded-full px-2.5 py-0.5">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              <b className="font-bold">{summary.password_invalid}</b> pwd invalid
            </span>
          )}
          <span className="inline-flex items-center gap-1 bg-violet-50 text-violet-800 border border-violet-200 rounded-full px-2.5 py-0.5 ml-auto" title="Total stock in this platform">
            <Storefront size={11} weight="fill" /> <b className="font-bold">{summary.total?.toLocaleString('en-IN') || 0}</b> total
          </span>
          {/* Capacity warning */}
          {summary.available_now <= 50 && summary.total > 100 && (
            <span className="inline-flex items-center gap-1 bg-rose-100 text-rose-800 border border-rose-300 rounded-full px-2.5 py-0.5 font-bold animate-pulse" data-testid="aas-pool-low-warn">
              ⚠️ Low pool — refill soon
            </span>
          )}
        </div>
      )}

      {/* Stock Health Dashboard */}
      {summary && (
        <div className="card-flat p-3" data-testid="aas-summary">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
              <ChartBar size={16} className="text-violet-600" weight="fill" /> Stock Health · {tabPalette.label}
            </h3>
            <button
              onClick={() => setShowHistory(s => !s)}
              className="text-xs text-violet-600 hover:text-violet-800 font-semibold inline-flex items-center gap-1"
              data-testid="aas-history-toggle"
            >
              <ClockCounterClockwise size={14} /> {showHistory ? 'Hide' : 'Show'} Usage History ({history.length})
            </button>
            <button
              onClick={() => { const next = !showAssignHistory; setShowAssignHistory(next); if (next) loadAssignHistory(); }}
              className="text-xs text-blue-600 hover:text-blue-800 font-semibold inline-flex items-center gap-1 ml-2"
              data-testid="aas-assign-history-toggle"
            >
              <ChartBar size={14} /> {showAssignHistory ? 'Hide' : 'Show'} Assignment History
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
            <div className="rounded-md bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 p-2">
              <div className="text-[10px] text-emerald-700 uppercase font-semibold">Available Now</div>
              <div className="text-2xl font-bold text-emerald-700" data-testid="aas-stat-available">{summary.available_now}</div>
            </div>
            <div className="rounded-md bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 p-2">
              <div className="text-[10px] text-blue-700 uppercase font-semibold">Held / Active</div>
              <div className="text-2xl font-bold text-blue-700" data-testid="aas-stat-held">{summary.held_active}</div>
            </div>
            <div className="rounded-md bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 p-2">
              <div className="text-[10px] text-amber-700 uppercase font-semibold">In Cooldown</div>
              <div className="text-2xl font-bold text-amber-700" data-testid="aas-stat-cooldown">{summary.in_cooldown}</div>
            </div>
            <div className="rounded-md bg-gradient-to-br from-red-50 to-red-100 border border-red-200 p-2">
              <div className="text-[10px] text-red-700 uppercase font-semibold">Pwd Invalid</div>
              <div className="text-2xl font-bold text-red-700" data-testid="aas-stat-pwd-invalid">{summary.password_invalid}</div>
            </div>
            <div className="rounded-md bg-gradient-to-br from-violet-50 to-violet-100 border border-violet-200 p-2">
              <div className="text-[10px] text-violet-700 uppercase font-semibold">Total Stock</div>
              <div className="text-2xl font-bold text-violet-700" data-testid="aas-stat-total">{summary.total}</div>
            </div>
          </div>
          {summary.bookings && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
              <div className="rounded bg-gray-50 px-3 py-1.5 flex items-center justify-between">
                <span className="text-xs text-gray-600">Total bookings done</span>
                <span className="font-bold text-gray-900">{summary.bookings.total_done}</span>
              </div>
              <div className="rounded bg-emerald-50 px-3 py-1.5 flex items-center justify-between" title="daily_limit ≤ 5 — safe bucket (next-month-1 cooldown)">
                <span className="text-xs text-emerald-700">Safe (≤5/account)</span>
                <span className="font-bold text-emerald-800">{summary.bookings.in_safe_bucket}</span>
              </div>
              <div className="rounded bg-orange-50 px-3 py-1.5 flex items-center justify-between" title="daily_limit > 5 — high bucket (30-day cooldown after limit)">
                <span className="text-xs text-orange-700">High (&gt;5/account · 30d cooldown)</span>
                <span className="font-bold text-orange-800">{summary.bookings.in_high_bucket}</span>
              </div>
            </div>
          )}
          {summary.top_users && summary.top_users.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              <span className="text-[10px] text-gray-500 font-bold uppercase mr-1 self-center">Currently Held By</span>
              {summary.top_users.map(u => (
                <span key={u.user_id} className="text-[11px] bg-blue-50 text-blue-800 border border-blue-200 px-2 py-0.5 rounded font-semibold" data-testid={`aas-topuser-${u.user_id}`}>
                  {u.name} <span className="text-blue-600">({u.role})</span> · <b>{u.active_count}</b>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Usage history */}
      {showHistory && history.length > 0 && (
        <div className="card-flat overflow-x-auto" data-testid="aas-history">
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead>Account Email</TableHead>
                <TableHead>Allocated To</TableHead>
                <TableHead>Agency</TableHead>
                <TableHead>When</TableHead>
                <TableHead>Approved By</TableHead>
                <TableHead>Mode</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.slice(0, 100).map((h, idx) => (
                <TableRow key={idx} data-testid={`aas-hist-${idx}`}>
                  <TableCell className="text-xs font-mono text-blue-700 truncate max-w-[220px]">{h.account_email || h.account_id}</TableCell>
                  <TableCell className="text-xs">{h.user_name} <span className="text-gray-400">({h.user_role})</span></TableCell>
                  <TableCell className="text-xs">{h.agency || '—'}</TableCell>
                  <TableCell className="text-xs text-gray-500">{formatDateTime(h.allocated_at)}</TableCell>
                  <TableCell className="text-xs text-gray-500">{h.approved_by_name || '—'}</TableCell>
                  <TableCell>
                    {h.auto_approved ? (
                      <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 rounded font-bold">AUTO</span>
                    ) : (
                      <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 rounded">manual</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Account assignment history (file-level auto-locks + cascades) */}
      {showAssignHistory && (
        <div className="card-flat overflow-x-auto" data-testid="aas-assign-history">
          <div className="px-3 py-2 text-[11px] text-gray-600 border-b bg-blue-50">
            Track who got which account & when. <b>Lock</b> = admin assigned file → staff. <b>Cascade</b> = staff distributed to sub-staff. <b>Release</b> = file unassigned.
          </div>
          {assignHistory.length === 0 ? (
            <div className="text-center text-xs text-gray-400 py-6">No assignment events yet</div>
          ) : (
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assignHistory.slice(0, 100).map((h, idx) => (
                  <TableRow key={idx} data-testid={`aas-assign-hist-${idx}`}>
                    <TableCell className="text-[11px] text-gray-500 whitespace-nowrap">{formatDateTime(h.created_at)}</TableCell>
                    <TableCell>
                      {h.action === 'lock' && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold">🔒 LOCK</span>}
                      {h.action === 'cascade' && <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded font-bold">↪ CASCADE</span>}
                      {h.action === 'release' && <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-bold">🔓 RELEASE</span>}
                    </TableCell>
                    <TableCell className="text-[11px] font-mono text-blue-700 truncate max-w-[200px]">{h.account_email || '—'}</TableCell>
                    <TableCell className="text-[11px] text-gray-500">{h.from_user_name || 'admin pool'}</TableCell>
                    <TableCell className="text-[11px] font-semibold">{h.to_user_name} <span className="text-gray-400 text-[10px]">({h.to_user_role})</span></TableCell>
                    <TableCell className="text-[11px] text-gray-500 truncate max-w-[180px]" title={h.file_name || h.file_id}>{h.file_name || (h.file_id ? h.file_id.slice(-6) : '—')}</TableCell>
                    <TableCell className="text-[11px] text-gray-500 truncate max-w-[280px]">{h.reason || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="card-flat p-2 flex items-center gap-1 w-fit">
        {[
          { id: 'amazon', label: '🟠 Amazon', color: 'bg-orange-600' },
          { id: 'tataneu', label: '🔴 TataNeu', color: 'bg-pink-600' },
        ].map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
              tab === t.id ? `${t.color} text-white` : 'text-gray-600 hover:bg-gray-50'
            }`}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card-flat p-3 flex items-center gap-3">
          <div className={`w-10 h-10 rounded-md ${tabPalette.tag} text-white grid place-items-center font-bold`}>
            {data.total_unique}
          </div>
          <div>
            <div className="text-xs text-gray-500">Unique Stock</div>
            <div className="text-sm font-semibold">{tabPalette.label} accounts</div>
          </div>
        </div>
        <div className="card-flat p-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-amber-500 text-white grid place-items-center font-bold">
            {data.duplicates_skipped || 0}
          </div>
          <div>
            <div className="text-xs text-gray-500">Auto-Skipped</div>
            <div className="text-sm font-semibold">Duplicate emails (merged)</div>
          </div>
        </div>
        <div className="card-flat p-3 flex items-center gap-3">
          <ShieldStar size={28} className="text-violet-600" weight="fill" />
          <div>
            <div className="text-xs text-gray-500">Login Password</div>
            <div className="text-sm font-semibold">Live merged: pwd + OTP code</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card-flat p-3 flex items-center gap-2">
        <MagnifyingGlass size={16} className="text-gray-400" />
        <Input
          placeholder="Search email / name / mobile…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="h-9 text-sm border-0 focus-visible:ring-0"
          data-testid="aas-search-input"
        />
        <span className="text-xs text-gray-500 whitespace-nowrap">{filtered.length} / {items.length}</span>
      </div>

      {filtered.length > RENDER_CAP && !showAll && (
        <div className="card-flat p-2 flex items-center justify-between text-xs bg-amber-50 border border-amber-200" data-testid="aas-render-cap-banner">
          <span className="text-amber-800">
            Showing first <b>{RENDER_CAP}</b> of <b>{filtered.length.toLocaleString()}</b> results · use search to filter, or click load all (slow on weak devices).
          </span>
          <Button size="sm" variant="outline" onClick={() => setShowAll(true)} className="h-7 text-xs border-amber-300 text-amber-800" data-testid="aas-show-all">
            Load All ({filtered.length.toLocaleString()})
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="card-flat overflow-x-auto" data-testid="aas-table">
        <Table className="table-dense">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Password</TableHead>
              <TableHead>TOTP Secret</TableHead>
              <TableHead>🔑 Login (pwd+OTP)</TableHead>
              <TableHead>Mobile</TableHead>
              <TableHead className="text-right">Usage</TableHead>
              <TableHead>Added By</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-6 text-gray-500">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-6 text-gray-500">Koi {tab} account nahi mila</TableCell></TableRow>
            ) : displayed.map(item => (
              <Fragment key={item.id}>
                <TableRow data-testid={`aas-row-${item.id}`}>
                  <TableCell className="text-sm font-semibold">
                    <div className="flex items-center gap-1">
                      <span>{item.name}</span>
                      {item.password_invalid && (
                        <span className="text-[9px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold border border-red-300" title={item.password_invalid_reason}>
                          🚫 PWD INVALID
                        </span>
                      )}
                    </div>
                    {item.duplicate_count > 1 && (
                      <span className="text-[9px] bg-amber-100 text-amber-700 px-1 rounded font-normal" title="duplicate emails merged">+{item.duplicate_count - 1} dup</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <button onClick={() => copy(item.email, 'Email')} className="text-xs text-blue-700 font-mono hover:underline" data-testid={`aas-copy-email-${item.id}`}>
                      {item.email || <span className="text-gray-400">—</span>}
                    </button>
                  </TableCell>
                  <TableCell>
                    {item.password ? (
                      <button onClick={() => copy(item.password, 'Password')} className="inline-flex items-center gap-1 text-xs font-mono text-amber-700 hover:underline" data-testid={`aas-copy-pwd-${item.id}`}>
                        <Copy size={11} /> {'•'.repeat(Math.min(6, item.password.length))}
                      </button>
                    ) : <span className="text-gray-400">—</span>}
                  </TableCell>
                  <TableCell>
                    {item.totp_secret ? (
                      <button onClick={() => copy(item.totp_secret, 'TOTP Secret')} className="inline-flex items-center gap-1 text-xs font-mono text-violet-700 hover:underline" data-testid={`aas-copy-totp-${item.id}`}>
                        <Copy size={11} /> {item.totp_secret.slice(0, 8)}…
                      </button>
                    ) : <span className="text-gray-400">—</span>}
                  </TableCell>
                  <TableCell>
                    {item.password && item.totp_secret ? (
                      <div className="flex flex-col gap-1">
                        <LoginPasswordWidget password={item.password} secret={item.totp_secret} label={`${item.email || 'Login'} (PWD 1)`} compact testId={`aas-login-${item.id}`} />
                        {item.password2 && (
                          <LoginPasswordWidget password={item.password2} secret={item.totp_secret} label={`${item.email || 'Login'} (PWD 2)`} compact testId={`aas-login2-${item.id}`} />
                        )}
                      </div>
                    ) : <span className="text-gray-400 text-xs italic">need pwd+totp</span>}
                  </TableCell>
                  <TableCell className="text-xs font-mono">{item.mobile || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell className="text-right text-xs">
                    {item.daily_usage_count || 0}{item.daily_limit ? ` / ${item.daily_limit}` : ''}
                  </TableCell>
                  <TableCell className="text-xs text-gray-600">{item.created_by_name || '—'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {!item.password_invalid && (
                        <Button size="sm" variant="ghost" onClick={() => flagPwdInvalid(item)} className="h-7 w-7 p-0" title="Flag password invalid" data-testid={`aas-flagpwd-${item.id}`}>
                          <Prohibit size={14} className="text-red-500" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openEdit(item)} className="h-7 w-7 p-0" title="Edit" data-testid={`aas-edit-${item.id}`}>
                        <Pencil size={14} className="text-blue-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleting(item)} className="h-7 w-7 p-0" title="Delete" data-testid={`aas-delete-${item.id}`}>
                        <Trash size={14} className="text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {/* Extras row (if any) */}
                {item.extras && Object.keys(item.extras).length > 0 && (
                  <TableRow className="bg-gray-50/50" data-testid={`aas-extras-${item.id}`}>
                    <TableCell colSpan={9} className="py-1.5 px-3">
                      <div className="text-[10px] text-gray-500 mb-1 font-bold">+EXTRA SHEET FIELDS</div>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(item.extras).map(([k, v]) => (
                          <span key={k} className="text-[10px] bg-blue-50 text-blue-800 border border-blue-200 px-1.5 py-0.5 rounded">
                            <b>{k}:</b> {String(v).slice(0, 40)}{String(v).length > 40 ? '…' : ''}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="aas-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit {editing?.platform === 'amazon' ? 'Amazon' : 'TataNeu'} Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {Object.keys(editFields || {}).map(k => (
              <div key={k}>
                <label className="text-xs text-gray-600 font-semibold">{k}</label>
                <Input
                  value={editFields[k] ?? ''}
                  onChange={e => setEditFields({ ...editFields, [k]: e.target.value })}
                  className="h-9 text-sm font-mono"
                  data-testid={`aas-edit-field-${k.replace(/\s+/g, '-')}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="aas-edit-cancel">Cancel</Button>
            <Button onClick={saveEdit} className="bg-violet-600 hover:bg-violet-700" data-testid="aas-edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="max-w-md" data-testid="aas-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Warning size={20} weight="fill" /> Confirm Delete
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700">
            Account <b>{deleting?.email || deleting?.name}</b> archive ho jayega. Data preserved rahega — sirf list me show nahi hoga.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)} data-testid="aas-delete-cancel">Cancel</Button>
            <Button onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="aas-delete-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Add new account dialog */}
      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="max-w-md" data-testid="aas-add-dialog">
          <DialogHeader>
            <DialogTitle>Add {tab === 'amazon' ? 'Amazon' : 'TataNeu'} Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Email / Account *</label>
              <Input value={addForm.Acount} onChange={e => setAddForm({ ...addForm, Acount: e.target.value })} placeholder="example@gmail.com" className="h-9 text-sm font-mono mt-1" data-testid="aas-add-email" />
              {addEmailDuplicate && (
                <div className="text-[11px] text-red-600 font-semibold mt-1">⚠ Yeh email already stock me hai — duplicate add nahi hoga</div>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Password *</label>
              <Input value={addForm.Pasword} onChange={e => setAddForm({ ...addForm, Pasword: e.target.value })} placeholder="account password" className="h-9 text-sm font-mono mt-1" data-testid="aas-add-pwd" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">TOTP Secret (Authenticator)</label>
              <Input value={addForm['OTP Cod']} onChange={e => setAddForm({ ...addForm, 'OTP Cod': e.target.value })} placeholder="Base32 secret e.g., NTSN CLTE…" className="h-9 text-sm font-mono mt-1" data-testid="aas-add-totp" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Mobile (optional)</label>
              <Input value={addForm.Mobile} onChange={e => setAddForm({ ...addForm, Mobile: e.target.value })} placeholder="10-digit" className="h-9 text-sm font-mono mt-1" data-testid="aas-add-mobile" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdding(false)} data-testid="aas-add-cancel">Cancel</Button>
            <Button onClick={submitAdd} disabled={addEmailDuplicate || !addForm.Acount || !addForm.Pasword} className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50" data-testid="aas-add-save">
              Add to Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
